import { LabelVariant } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_DATETIME_FORMAT } from 'constants/date';
import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import { IntegrationProviderKey } from 'constants/integrations';
import { PhysicalMailClassEnum } from 'constants/loanActionEditor';
import {
  DeliveryStatus,
  FormDeliveryChannelType,
  PhysicalMailDeliveryStatus,
  SignatureRequestStatus,
} from 'constants/loanApplication';
import { deliveryStatusOptions } from 'constants/options';
import { getUserDisplayName } from 'utils/user';

import {
  DISCLOSURE_ACTIVITY_SUMMARY,
  DISCLOSURE_SEND_ATTESTATION_EVENT_TYPES,
  GENERATED_OUTSIDE_SENT_TOOLTIP,
  GENERATED_OUTSIDE_STATUS_LABEL,
  GENERATED_OUTSIDE_TOOLTIP,
  REVIEW_PENDING_PACKAGE_STATUSES,
  REVIEW_RECIPIENT_PENDING_STATUSES,
} from './DisclosureDelivery.constants';

const SIGNED_PACKAGE_STATUSES = [
  SignatureRequestStatus.COMPLETED,
  SignatureRequestStatus.SIGNED_WITHOUT_DOCUMENT,
];

const PACKAGE_SUBTITLE_DATE_FORMAT = 'MMM D, YYYY · h:mm A';

export const getDateTimeFormat = date =>
  date ? dayjs(date).format(COMMON_DATETIME_FORMAT) : 'N/A';

const getRecipientNoun = ({ count, isSignable }) => {
  const noun = isSignable ? 'signer' : 'recipient';

  return count === 1 ? noun : `${noun}s`;
};

export const getPackageSubtitle = (
  { sentDate, scheduledDeliveryDate, signatureItems, isGeneratedOutside, isSignable = true } = {},
  { isScheduled = false } = {},
) => {
  const signerCount = signatureItems?.length ?? 0;
  // Generated-outside packages don't track individual recipients on our side, so the signer count
  // segment is dropped entirely rather than shown as a misleading "0 signers".
  const signerSuffix = isGeneratedOutside
    ? ''
    : ` · ${signerCount} ${getRecipientNoun({ count: signerCount, isSignable })}`;

  if (isScheduled) {
    const when = scheduledDeliveryDate
      ? dayjs(scheduledDeliveryDate).format(PACKAGE_SUBTITLE_DATE_FORMAT)
      : null;

    return when ? `Scheduled ${when}${signerSuffix}` : `Scheduled delivery${signerSuffix}`;
  }

  if (!sentDate) {
    return `Not sent yet${signerSuffix}`;
  }

  return `Sent ${dayjs(sentDate).format(PACKAGE_SUBTITLE_DATE_FORMAT)}${signerSuffix}`;
};

const PACKAGE_STATUS_LABEL = {
  [SignatureRequestStatus.COMPLETED]: { title: 'Completed', variant: LabelVariant.GREEN },
  [SignatureRequestStatus.SIGNED_WITHOUT_DOCUMENT]: {
    title: 'Completed',
    variant: LabelVariant.GREEN,
  },
  // Deliberately absent from SIGNED_PACKAGE_STATUSES: this package completed without collecting a
  // signature, so it must not read as signed in the chip variant or the generated-outside tooltip.
  [SignatureRequestStatus.DELIVERED]: { title: 'Delivered', variant: LabelVariant.GREEN },
  [SignatureRequestStatus.CANCELED]: { title: 'Canceled', variant: LabelVariant.GRAY },
  [SignatureRequestStatus.SCHEDULED]: { title: 'Scheduled', variant: LabelVariant.BLUE },
  [SignatureRequestStatus.BORROWERS_PENDING]: {
    title: 'Awaiting signatures',
    variant: LabelVariant.PURPLE,
  },
  [SignatureRequestStatus.LOAN_OFFICER_PENDING]: {
    title: 'Awaiting signatures',
    variant: LabelVariant.PURPLE,
  },
};

const getReviewPackageStatusLabel = ({ status, signatureItems }) => {
  if (!REVIEW_PENDING_PACKAGE_STATUSES.includes(status)) {
    return null;
  }

  const isReviewedByEveryRecipient =
    !!signatureItems?.length && signatureItems.every(({ consentedDate }) => !!consentedDate);

  return isReviewedByEveryRecipient
    ? { title: 'Reviewed', variant: LabelVariant.GREEN }
    : { title: 'Awaiting review', variant: LabelVariant.PURPLE };
};

// An unrecognized status resolves to the neutral placeholder, never to the pending label: borrowing
// "Awaiting signatures" for a status nobody mapped is what let a cancelled package read as live.
export const getPackageStatusLabel = ({
  status,
  isGeneratedOutside,
  isSignable = true,
  signatureItems,
} = {}) => {
  const reviewLabel = isSignable ? null : getReviewPackageStatusLabel({ status, signatureItems });
  const resolvedLabel = reviewLabel ??
    PACKAGE_STATUS_LABEL[status] ?? {
      title: '-',
      variant: LabelVariant.GRAY,
    };

  // Cancellation outranks the FH-1435 generated-outside label — otherwise a cancelled outside-generated
  // package paints an active-looking blue chip with no cancellation signal anywhere on the row.
  if (status === SignatureRequestStatus.CANCELED) {
    return resolvedLabel;
  }

  if (!isGeneratedOutside) {
    return resolvedLabel;
  }

  // FH-1435: a generated-outside package that is sent-but-not-yet-signed carries the Sent (blue) variant
  // to match its recipients' "Sent" tag; a signed one keeps the Completed (green) variant.
  return {
    title: GENERATED_OUTSIDE_STATUS_LABEL,
    variant: SIGNED_PACKAGE_STATUSES.includes(status) ? LabelVariant.GREEN : LabelVariant.BLUE,
  };
};

const SIGNER_STATUS_VARIANT = {
  [DeliveryStatus.SIGNED]: LabelVariant.GREEN,
  [DeliveryStatus.SENT]: LabelVariant.BLUE,
  [DeliveryStatus.OPENED]: LabelVariant.ORANGE,
  [DeliveryStatus.CANCELED]: LabelVariant.RED,
  [DeliveryStatus.NOT_SENT]: LabelVariant.GRAY,
  [PhysicalMailDeliveryStatus.AWAITING_DISPATCH]: LabelVariant.GRAY,
  [PhysicalMailDeliveryStatus.MAILED]: LabelVariant.BLUE,
  [PhysicalMailDeliveryStatus.IN_TRANSIT]: LabelVariant.ORANGE,
  [PhysicalMailDeliveryStatus.DELIVERED]: LabelVariant.GREEN,
  [PhysicalMailDeliveryStatus.RETURNED_TO_SENDER]: LabelVariant.RED,
  [PhysicalMailDeliveryStatus.MAIL_FAILED]: LabelVariant.RED,
};

const PHYSICAL_MAIL_STATUS_TITLE = {
  [PhysicalMailDeliveryStatus.AWAITING_DISPATCH]: 'Awaiting dispatch',
  [PhysicalMailDeliveryStatus.MAILED]: 'Mailed',
  [PhysicalMailDeliveryStatus.IN_TRANSIT]: 'In Transit',
  [PhysicalMailDeliveryStatus.DELIVERED]: 'Delivered',
  [PhysicalMailDeliveryStatus.RETURNED_TO_SENDER]: 'Returned to Sender',
  [PhysicalMailDeliveryStatus.MAIL_FAILED]: 'Mail Failed',
};

// FH-1394: the email/portal pre-dispatch state has no entry in deliveryStatusOptions (a filter list) or the
// physical-mail title map, so it resolves its label here.
const NON_DISPATCHED_STATUS_TITLE = {
  [DeliveryStatus.NOT_SENT]: 'Not sent yet',
};

// A review-only recipient has nothing to sign, so the signing lane's Opened ("Awaiting Signature")
// never applies; the Read date is what moves it forward.
const getReviewRecipientStatusLabel = ({ status, consentedDate }) => {
  if (!REVIEW_RECIPIENT_PENDING_STATUSES.includes(status)) {
    return null;
  }

  return consentedDate
    ? { title: 'Read', variant: LabelVariant.GREEN }
    : { title: 'Sent', variant: SIGNER_STATUS_VARIANT[DeliveryStatus.SENT] };
};

// An unmapped status resolves to the placeholder rather than to `status` itself: the raw value is a
// backend VO discriminator (`CancelledBySystem.AfterSending`), never presentable copy.
const getSigningRecipientStatusLabel = status => ({
  title:
    deliveryStatusOptions.find(option => option.value === status)?.name ||
    PHYSICAL_MAIL_STATUS_TITLE[status] ||
    NON_DISPATCHED_STATUS_TITLE[status] ||
    '-',
  variant: SIGNER_STATUS_VARIANT[status] ?? LabelVariant.GRAY,
});

export const getSignerStatusLabel = (status, { isSignable = true, consentedDate } = {}) =>
  (!isSignable && getReviewRecipientStatusLabel({ status, consentedDate })) ||
  getSigningRecipientStatusLabel(status);

export const getIsGeneratedOutsideSignedTooltipVisible = ({ status, isGeneratedOutside } = {}) =>
  !!isGeneratedOutside && status === DeliveryStatus.SIGNED;

export const getGeneratedOutsidePackageTooltip = status =>
  SIGNED_PACKAGE_STATUSES.includes(status)
    ? GENERATED_OUTSIDE_TOOLTIP
    : GENERATED_OUTSIDE_SENT_TOOLTIP;

const MAIL_PROVIDER_LABEL = {
  [IntegrationProviderKey.LOB]: 'Lob',
};

const getMailProviderLabel = dispatchProviderKey =>
  MAIL_PROVIDER_LABEL[dispatchProviderKey] ?? 'the mail provider';

// The "Sent" column tooltip branches per-recipient on delivery channel — a column-level static
// tooltip config can't express this (it only ever renders on the column header), so this backs a
// per-row Tooltip wrapping the Sent cell instead.
export const getSentTooltip = ({ channel, dispatchProviderKey, mailStatusDate, sentDate } = {}) => {
  if (channel !== FormDeliveryChannelType.MAIL) {
    return 'The disclosures were emailed to the recipient';
  }

  const providerLabel = getMailProviderLabel(dispatchProviderKey);
  const mailedDate = mailStatusDate ?? sentDate;

  return mailedDate
    ? `Mailed via ${providerLabel} on ${getDateTimeFormat(mailedDate)}`
    : `Mailed via ${providerLabel}`;
};

// Mirrors the DisclosureSignerTable `isDownloadable` gating pattern: the proof-of-mailing artifact
// only exists once the mail provider has accepted a Certified + Return Receipt letter and recorded a
// dispatch reference for it.
export const getIsMailProofLinkAvailable = ({ channel, mailClass, dispatchReference } = {}) =>
  channel === FormDeliveryChannelType.MAIL &&
  mailClass === PhysicalMailClassEnum.CERTIFIED_RETURN_RECEIPT &&
  !!dispatchReference;

const TERMINAL_RECIPIENT_STATUSES = [DeliveryStatus.SIGNED, DeliveryStatus.CANCELED];

// Gated on the owning package as well as the row: remediation reissues a signing link, and a
// recipient under a cancelled package does not always carry a cancelled status of its own (physical
// mail is never cancelled per-recipient; a package cancelled before dispatch leaves its rows un-sent).
export const getIsRecipientRemediable = ({ status, packageStatus } = {}) =>
  packageStatus !== SignatureRequestStatus.CANCELED &&
  !TERMINAL_RECIPIENT_STATUSES.includes(status);

export const getIsRecipientPreviewable = ({ documentId, isFenced, packageStatus, status } = {}) =>
  _.isNil(documentId) &&
  isFenced &&
  REVIEW_PENDING_PACKAGE_STATUSES.includes(packageStatus) &&
  status !== DeliveryStatus.CANCELED;

export const getIsRemediationUnavailableStatus = statusCode =>
  statusCode === HTTP_RESPONSE_STATUS_CODES.NOT_FOUND ||
  statusCode === HTTP_RESPONSE_STATUS_CODES.METHOD_NOT_ALLOWED;

// The disclosure activity endpoint (DEV-4626) may not be deployed yet: an unknown route 404s and
// the TPO gating not being live 403s. Either means "not available" — the strip degrades to
// Issued-by-only with no toast and no crash (task item 1 / Part C degradation rules).
export const getIsDisclosureActivityUnavailableStatus = statusCode =>
  statusCode === HTTP_RESPONSE_STATUS_CODES.NOT_FOUND ||
  statusCode === HTTP_RESPONSE_STATUS_CODES.FORBIDDEN;

// The issuer ("Issued by") actor = the userId of the newest send-step attestation entry. There is
// no dedicated "sent" event, so the attestation signed at send time is the closest reliable proxy.
// Returns null when no attestation exists (genuinely underivable → the strip hides "Issued by").
export const getSentByUserIdFromActivity = (entries = []) => {
  const attestations = (entries ?? []).filter(entry =>
    DISCLOSURE_SEND_ATTESTATION_EVENT_TYPES.includes(entry?.eventType),
  );

  if (!attestations.length) {
    return null;
  }

  const newest = attestations.reduce((latest, entry) =>
    new Date(entry.createdAt) > new Date(latest.createdAt) ? entry : latest,
  );

  return newest.userId ?? null;
};

// The employee directory (store/users) only holds the master company's employees, so a broker
// (sub-company) actor who prepares/attests TPO disclosures never resolves and renders as
// "Unknown user". Reduce the loan's sub-company users to the same id -> { name } shape the audit
// strip/trail resolve against, so those actors resolve too.
export const buildActorDirectoryFromSubCompanyUsers = (users = []) =>
  (users ?? []).reduce(
    (directory, user) =>
      user?.id ? { ...directory, [user.id]: { name: getUserDisplayName(user) } } : directory,
    {},
  );

export const getDisclosureActivitySummary = entry =>
  DISCLOSURE_ACTIVITY_SUMMARY[entry?.eventType] ?? entry?.description ?? 'Disclosure activity';

// Newest-first for display in the audit-trail dialog.
export const sortDisclosureActivityByNewest = (entries = []) =>
  [...(entries ?? [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
